/**
 * Drive one offer through its lifecycle — the offer analogue of the pipeline's
 * `ApplicationStageControls` and the interview's `InterviewStatusControls`, and the same
 * rule: **the illegal actions have no DOM node.**
 *
 * `offerActions(status)` is the whole list, mapped once. There is no loop computing a
 * `disabled` flag, because a disabled attribute is a rendering decision an operator
 * removes in devtools, and a greyed control asserts "the system will not let you" about
 * an action that exists for nobody. `accepted`, `declined`, `rescinded` and `expired`
 * are terminal — their action list is `[]` — so a settled offer renders no buttons and
 * one sentence.
 *
 * Unlike the interview machine, an offer's moves are three distinct verbs on three
 * distinct endpoints (approve / send / respond), not one generic transition. So the
 * control emits an `OfferAction` and the screen routes it to the right call; the button
 * label is the verb the press performs, never the name of a status.
 *
 * ── The refusal is the authority ─────────────────────────────────────────────
 *
 * A stale detail can still issue an action the server refuses `409
 * WORKFORCE_INVALID_STATE_TRANSITION`, and that refusal is rendered — the server's own
 * sentence through `ApiErrorNote`, and the server's own `allowed_transitions[]` printed
 * beside it rather than re-derived from this client's copy. Every offer write needs
 * `recruiting.manage_offers`; a caller without it is refused and the shared
 * `PermissionDenied` (via `ApiErrorNote`) renders it.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { WorkforceApiError } from '../../api/errors';
import { allowedTransitionsFromRefusal } from '../../api/useApiMutation';
import {
  offerActions,
  isTerminalOfferStatus,
  OFFER_ACTION_LABEL,
  type OfferAction,
  type OfferStatus,
} from '../../recruiting/offer';
import { ApiErrorNote } from '../../states/ApiErrorNote';

export interface OfferStatusControlsProps {
  readonly offerId: string;
  readonly status: OfferStatus;
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly onAction: (action: OfferAction) => void;
}

export function OfferStatusControls({
  offerId,
  status,
  pending,
  error,
  onAction,
}: OfferStatusControlsProps): ReactNode {
  const { t } = useI18n();
  const actions = offerActions(status);
  const serverAllowed = error === null ? [] : allowedTransitionsFromRefusal(error);

  return (
    <div className="wp-board__moves" data-testid={`w5-controls-${offerId}`}>
      {isTerminalOfferStatus(status) ? (
        <p className="wp-small wp-muted" data-testid={`w5-terminal-${offerId}`}>
          {t('recruiting.offers.status.terminal')}
        </p>
      ) : (
        <div className="wp-toggle">
          {actions.map((action) => (
            <button
              key={action}
              className="wp-button"
              type="button"
              data-testid={`w5-action-${action}-${offerId}`}
              disabled={pending}
              onClick={() => {
                onAction(action);
              }}
            >
              {t(OFFER_ACTION_LABEL[action])}
            </button>
          ))}
        </div>
      )}

      {error === null ? null : (
        <>
          <ApiErrorNote
            error={error}
            subject={t('recruiting.offers.subjectWord')}
            testId={`w5-action-error-${offerId}`}
          />
          {serverAllowed.length === 0 ? null : (
            <p className="wp-small" data-testid={`w5-action-allowed-${offerId}`}>
              {t('recruiting.offers.status.serverAllowed', { list: serverAllowed.join(', ') })}
            </p>
          )}
        </>
      )}
    </div>
  );
}
